import React, { Suspense, lazy, useCallback, useState } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';

const loadSupportMenuPanel = () => import('./SupportMenuPanel');
// Popover + panel ship in their own chunk, fetched on first intent, so the header's first paint stays light.
const SupportMenuPanel = lazy(loadSupportMenuPanel);

export const SUPPORT_MENU_PANEL_ID = 'yovo-support-menu-panel';

const DEFAULT_ICON_SX = { color: 'var(--yovo-base-white)', fontSize: '24px' };

export type SupportMenuProps = {
  /** Class for the trigger button, e.g. the header icon-button class. */
  triggerClassName?: string;
  /** Headset icon colour/size for the surface it sits on. */
  iconSx?: SxProps<Theme>;
};

/**
 * Header support trigger (headset icon) that opens the "How can we help?" popover
 * with Email / WhatsApp shortcuts. Contact details come from config/supportContact.
 */
const SupportMenu = ({ triggerClassName, iconSx = DEFAULT_ICON_SX }: SupportMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleToggle = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const target = e.currentTarget;
    setAnchorEl((prev) => (prev ? null : target));
  }, []);
  const handleClose = useCallback(() => setAnchorEl(null), []);

  return (
    <>
      <button
        type="button"
        className={triggerClassName}
        aria-label="Customer support"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? SUPPORT_MENU_PANEL_ID : undefined}
        onPointerEnter={loadSupportMenuPanel}
        onPointerDown={loadSupportMenuPanel}
        onFocus={loadSupportMenuPanel}
        onClick={handleToggle}
      >
        <SupportAgentOutlinedIcon
          aria-hidden="true"
          sx={[{ pointerEvents: 'none' }, ...(Array.isArray(iconSx) ? iconSx : [iconSx])]}
        />
      </button>
      {open && (
        <Suspense fallback={null}>
          <SupportMenuPanel id={SUPPORT_MENU_PANEL_ID} anchorEl={anchorEl} onClose={handleClose} />
        </Suspense>
      )}
    </>
  );
};

export default SupportMenu;
